import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown>;
}

/**
 * Dynamic per-page SEO meta tags.
 * Updates document head on mount, restores defaults on unmount.
 */
export function SEOHead({
  title,
  description,
  canonical,
  ogType = 'website',
  ogImage = 'https://opsgpt.app/og-image.png',
  noindex = false,
  jsonLd,
}: SEOHeadProps) {
  useEffect(() => {
    const prevTitle = document.title;

    // Title
    document.title = title;

    // Helper to set/create meta tags
    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    // Primary SEO
    setMeta('name', 'description', description);
    if (noindex) setMeta('name', 'robots', 'noindex, nofollow');

    // Open Graph
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:image', ogImage);
    if (canonical) setMeta('property', 'og:url', canonical);

    // Twitter
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', ogImage);

    // Canonical
    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonical) {
      if (!canonicalEl) {
        canonicalEl = document.createElement('link');
        canonicalEl.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute('href', canonical);
    }

    // JSON-LD
    let jsonLdEl: HTMLScriptElement | null = null;
    if (jsonLd) {
      jsonLdEl = document.createElement('script');
      jsonLdEl.type = 'application/ld+json';
      jsonLdEl.textContent = JSON.stringify(jsonLd);
      jsonLdEl.id = 'dynamic-jsonld';
      // Remove existing dynamic one
      const existing = document.getElementById('dynamic-jsonld');
      if (existing) existing.remove();
      document.head.appendChild(jsonLdEl);
    }

    return () => {
      document.title = prevTitle;
      if (jsonLdEl) jsonLdEl.remove();
    };
  }, [title, description, canonical, ogType, ogImage, noindex, jsonLd]);

  return null;
}
